import { styled } from 'styled-system/jsx'

const PageShell = styled('main', {
  base: {
    minHeight: '100vh',
    color: 'app.fg',
    display: 'flex',
    justifyContent: 'center',
  },
  variants: {
    view: {
      full: {
        backgroundColor: 'app.bg',
        paddingX: { base: '16px', md: '32px' },
        paddingY: { base: '24px', md: '48px' },
      },
      minimal: {
        backgroundColor: 'transparent',
        padding: '0',
      },
    },
  },
  defaultVariants: { view: 'full' },
})

const LoadingState = styled('div', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    color: 'app.fgMuted',
    paddingY: '64px',
  },
})

const ErrorState = styled('div', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    color: 'book.danger',
    paddingY: '64px',
    maxWidth: '480px',
    textAlign: 'center',
  },
})

const TwoColumnLayout = styled('div', {
  base: {
    width: '100%',
    display: 'grid',
    overflow: 'hidden',
  },
  variants: {
    view: {
      full: {
        maxWidth: '1080px',
        backgroundColor: 'app.surface',
        borderRadius: 'lg',
        boxShadow:
          '0 1px 2px rgba(15, 23, 42, 0.04), 0 8px 24px rgba(15, 23, 42, 0.06)',
        gridTemplateColumns: { base: '1fr', md: '1fr 1.2fr' },
        border: '1px solid',
        borderColor: 'app.border',
      },
      minimal: {
        maxWidth: 'none',
        backgroundColor: 'transparent',
        borderRadius: '0',
        boxShadow: 'none',
        gridTemplateColumns: '1fr',
        border: 'none',
      },
    },
  },
  defaultVariants: { view: 'full' },
})

const LeftColumn = styled('section', {
  base: {
    padding: { base: '24px', md: '32px' },
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    borderRight: { base: 'none', md: '1px solid' },
    borderBottom: { base: '1px solid', md: 'none' },
    borderColor: { base: 'app.border', md: 'app.border' },
  },
})

const RightColumn = styled('section', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '480px',
  },
  variants: {
    view: {
      full: { padding: { base: '24px', md: '32px' } },
      minimal: { padding: { base: '16px', md: '24px' } },
    },
  },
  defaultVariants: { view: 'full' },
})

const EventTypeImage = styled('img', {
  base: {
    width: '100%',
    aspectRatio: '16 / 9',
    objectFit: 'cover',
    borderRadius: 'md',
    backgroundColor: 'book.primarySoft',
  },
})

const EventTypeImagePlaceholder = styled('div', {
  base: {
    width: '100%',
    aspectRatio: '16 / 9',
    borderRadius: 'md',
    background: 'linear-gradient(135deg, #E6F0FF 0%, #BCD6FF 100%)',
  },
})

const EventTypeTitle = styled('h1', {
  base: {
    fontFamily: 'display',
    fontSize: { base: '24px', md: '28px' },
    fontWeight: 600,
    color: 'app.fg',
    margin: 0,
    lineHeight: 1.2,
  },
})

const EventTypeProperty = styled('div', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    fontWeight: 500,
    color: 'app.accent',
    marginTop: '-8px',
  },
})

const EventTypeDescription = styled('div', {
  base: {
    fontFamily: 'body',
    fontSize: '15px',
    lineHeight: 1.6,
    color: 'app.fgMuted',
    '& a': {
      color: 'app.accent',
      textDecoration: 'underline',
    },
    '& p': { margin: '0 0 8px 0' },
    '& strong': { fontWeight: 600, color: 'app.fg' },
    '& em': { fontStyle: 'italic' },
    '& ul, & ol': { paddingLeft: '20px', margin: '8px 0' },
  },
})

const ComingSoon = styled('div', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    color: 'app.fgMuted',
    fontStyle: 'italic',
    padding: '24px',
    border: '1px dashed',
    borderColor: 'app.border',
    borderRadius: 'md',
    textAlign: 'center',
  },
})

const BackButton = styled('button', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    fontWeight: 500,
    color: 'app.fgMuted',
    backgroundColor: 'transparent',
    border: 'none',
    padding: '4px 8px',
    marginLeft: '-8px',
    marginBottom: '12px',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    borderRadius: 'sm',
    width: 'fit-content',
    transition: 'color 160ms',
    _hover: {
      color: 'app.accent',
    },
  },
})

export {
  PageShell,
  LoadingState,
  ErrorState,
  TwoColumnLayout,
  LeftColumn,
  RightColumn,
  EventTypeImage,
  EventTypeImagePlaceholder,
  EventTypeTitle,
  EventTypeProperty,
  EventTypeDescription,
  ComingSoon,
  BackButton,
}
